export type ModuleNavItem = {
  id: string;
  label: string;
  path: string;
  requiredPermission: string;
  moduleId: string;
};

/**
 * No Module exists yet — every future Module registers its own nav item here when its own
 * StepLedger ships it (Resolution 4). The running app therefore always filters an empty registry
 * today; this is the routed-section/gated-navigation PATTERN (FR-6.1, FR-6.2), realized and
 * unit-proven with fixtures ahead of any real consumer.
 */
export const moduleRegistry: readonly ModuleNavItem[] = [];

export type NavCapabilities = {
  entitledModuleIds: ReadonlySet<string>;
  heldPermissions: ReadonlySet<string>;
};

/**
 * The gated-navigation predicate for ONE module (FR-6.2, AC-17): visible only when its `moduleId`
 * is entitled AND its `requiredPermission` is held. This is convenience only: every Module route
 * independently enforces its own entitlement and permission checks on the server (FR-6.3),
 * regardless of what it returns.
 *
 * It is separated from `visibleModules` below so the module catalog's `registry` gate can reuse the
 * same decision rather than restate it — one predicate, two callers, no chance of the rail and the
 * filter disagreeing. Typed on the two fields it actually reads, so a caller holding a richer
 * object (or a bare gate descriptor) can pass it without constructing a whole `ModuleNavItem`.
 */
export function isModuleVisible(
  module: Pick<ModuleNavItem, 'moduleId' | 'requiredPermission'>,
  capabilities: NavCapabilities,
): boolean {
  return (
    capabilities.entitledModuleIds.has(module.moduleId) &&
    capabilities.heldPermissions.has(module.requiredPermission)
  );
}

/**
 * Gated-navigation pure filter (FR-6.2, AC-17) — `isModuleVisible` applied across a registry, with
 * a module omitted when either half fails.
 */
export function visibleModules(
  modules: readonly ModuleNavItem[],
  capabilities: NavCapabilities,
): readonly ModuleNavItem[] {
  return modules.filter((module) => isModuleVisible(module, capabilities));
}
